import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  viewChild,
} from '@angular/core';

import { debounceTime, distinctUntilChanged, fromEvent, map, Observable, switchMap } from 'rxjs';

import { ITag } from '../../../../modules/chat/interfaces/ITag';
import { MessagingApiService } from '../../../../modules/chat/services/messaging-api/messaging-api.service';
import { BaseComponent } from '../../../shared/components/base/base.component';
import { IMessageableEntity } from '../../interfaces/IMessageableEntity';

@Component({
  selector: 'ngx-users-search',
  templateUrl: './users-search.component.html',
  styleUrls: ['./users-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UsersSearchComponent extends BaseComponent implements OnInit {
  private dataService = inject(MessagingApiService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @Input() useExternalUsers: boolean;
  @Input() canUseFilter = true;
  @Input() usersSelected: IMessageableEntity[] = [];
  @Input() usersDisabled: IMessageableEntity[] = [];
  @Input() events: Observable<void>;
  @Input() maxUsersListHeigt = '25vh';
  @Input() useGroup = true;
  @Input() showSelectAll = true;
  @Input() selectOnlyOne = false;
  @Input() hideCheckbox = false;
  @Input() hideHeader = false;

  @Input() set page(value: number) {
    this.pageSignal.set(value);
  }
  @Input() set text(value: string) {
    this.textSignal.set(value ?? '');
  }
  @Input() set users(value: IMessageableEntity[]) {
    this.usersSignal.set(value ?? []);
  }
  @Input() set isLoadingUsers(value: boolean) {
    this.isLoadingUsersSignal.set(value);
  }
  @Input() set tagsFilter(value: ITag[]) {
    this.tagsFilterSignal.set(value ?? []);
  }
  @Input() set isLoadingAllCheck(value: boolean) {
    this.isLoadingAllCheckSignal.set(value);
  }
  @Input() set showFilterInput(value: boolean) {
    this.showFilterInputSignal.set(value);
  }

  @Output() selectOneUserEvent = new EventEmitter<IMessageableEntity | undefined>();
  @Output() usersSelectedChange = new EventEmitter<IMessageableEntity[]>();

  pageSignal = signal(1);
  textSignal = signal('');
  usersSignal = signal<IMessageableEntity[]>([]);
  isLoadingUsersSignal = signal(false);
  isLoadingAllCheckSignal = signal(false);
  showFilterTagsSignal = signal(false);
  showFilterInputSignal = signal(false);
  tagsFilterSignal = signal<ITag[]>([]);

  usersSearchInput = viewChild<ElementRef<HTMLInputElement>>('usersSearch');

  constructor() {
    super();
    effect((onCleanup) => {
      const input = this.usersSearchInput();
      if (!input) {
        return;
      }
      const subscription = fromEvent<KeyboardEvent>(input.nativeElement, 'keyup')
        .pipe(
          map((event: KeyboardEvent) => (event.target as HTMLInputElement).value),
          debounceTime(500),
          distinctUntilChanged(),
          switchMap((searchText: string) => {
            this.pageSignal.set(1);
            this.usersSignal.set([]);
            this.textSignal.set(searchText);
            this.isLoadingUsersSignal.set(true);
            return this.dataService.searchUsers({
              searchTerm: searchText,
              page: this.pageSignal(),
              size: 20,
              tags: this.tagsFilterSignal(),
              useGroup: this.useGroup,
            });
          })
        )
        .subscribe({
          next: (data) => {
            this.usersSignal.set(this.setDisabledUsers(data));
            this.isLoadingUsersSignal.set(false);
          },
          error: () => {
            this.isLoadingUsersSignal.set(false);
          },
        });
      onCleanup(() => subscription.unsubscribe());
    });
  }

  ngOnInit(): void {
    if (!this.useExternalUsers) {
      this.searchUsers();
      if (this.events) {
        const eventsSubscription = this.events.subscribe(() => {
          this.pageSignal.set(1);
          this.usersSignal.set([]);
          this.searchUsers();
        });
        this.addSubscription(eventsSubscription);
      }
    }
  }

  searchUsers(): void {
    this.isLoadingUsersSignal.set(true);
    this.dataService
      .searchUsers({
        searchTerm: this.textSignal(),
        page: this.pageSignal(),
        size: 20,
        tags: this.tagsFilterSignal(),
        useGroup: this.useGroup,
      })
      .subscribe({
        next: (data) => {
          this.usersSignal.update((users) => this.setDisabledUsers([...users, ...data]));
          this.isLoadingUsersSignal.set(false);
        },
        error: () => {
          this.isLoadingUsersSignal.set(false);
        },
      });
  }

  selectAllUsers(): void {
    if (!this.useExternalUsers) {
      this.isLoadingUsersSignal.set(true);
      this.isLoadingAllCheckSignal.set(true);
      this.dataService
        .searchUsers({
          searchTerm: this.textSignal(),
          page: 1,
          size: 999,
          tags: this.tagsFilterSignal(),
          useGroup: this.useGroup,
        })
        .subscribe({
          next: (data) => {
            this.clearSelectedusers();
            this.addAllUsersSelected(this.setDisabledUsers(data));
            this.isLoadingUsersSignal.set(false);
            this.isLoadingAllCheckSignal.set(false);
          },
          error: () => {
            this.isLoadingUsersSignal.set(false);
            this.isLoadingAllCheckSignal.set(false);
          },
        });
    }
  }

  selectMemberDirect(user: IMessageableEntity): void {
    if (this.hideCheckbox) {
      this.selectMember(user);
    }
  }

  selectMember(user: IMessageableEntity): void {
    const index = this.usersSelected.findIndex((item) => item.uid === user.uid);
    if (this.selectOnlyOne) {
      if (index === -1) {
        this.usersSelected = [user];
        this.selectOneUserEvent.emit(user);
      } else {
        this.usersSelected = [];
        this.selectOneUserEvent.emit(undefined);
      }
      this.publishUsersSelected();
      this.clearSearchInput();
      return;
    }
    if (index === -1) {
      this.usersSelected.push(user);
    } else {
      this.usersSelected.splice(index, 1);
    }
    this.publishUsersSelected();
  }

  clearSearchInput(): void {
    const input = this.usersSearchInput();
    if (input) {
      input.nativeElement.value = '';
    }
    this.textSignal.set('');
    this.pageSignal.set(1);
    this.usersSignal.set([]);
    this.searchUsers();
  }

  nextPage(): void {
    if (!this.useExternalUsers) {
      this.pageSignal.update((page) => page + 1);
      this.searchUsers();
    }
  }

  isUserSelected(user: IMessageableEntity): boolean {
    return this.usersSelected.some((e) => e.uid === user.uid);
  }

  selectAll(event: { checked: boolean }): void {
    if (event.checked) {
      this.selectAllUsers();
      if (this.useExternalUsers) {
        this.clearSelectedusers();
        this.addAllUsersSelected(this.usersSignal());
      }
    } else {
      this.clearSelectedusers();
    }
  }

  addAllUsersSelected(newUsers: IMessageableEntity[]): void {
    for (const user of newUsers) {
      if (!user.disabled && !this.isUserSelected(user)) {
        this.usersSelected.push(user);
      }
    }
    this.publishUsersSelected();
  }

  clearSelectedusers(): void {
    while (this.usersSelected.length) {
      this.usersSelected.pop();
    }
    this.publishUsersSelected();
  }

  private publishUsersSelected(): void {
    this.usersSelectedChange.emit([...this.usersSelected]);
    this.changeDetectorRef.markForCheck();
  }

  tagsChanged(tags: ITag[]): void {
    this.tagsFilterSignal.set(tags);
    this.usersSignal.set([]);
    this.pageSignal.set(1);
    this.textSignal.set('');
    this.searchUsers();
  }

  toggleShowFilterInput(): void {
    this.showFilterInputSignal.update((value) => !value);
    this.textSignal.set('');
  }

  toggleShowFilterTags(): void {
    this.showFilterTagsSignal.update((value) => !value);
    this.tagsFilterSignal.set([]);
  }

  setDisabledUsers(users: IMessageableEntity[]): IMessageableEntity[] {
    if (this.usersDisabled?.length) {
      return users.map((user) => {
        user.disabled = this.usersDisabled.map((e) => e.uid).includes(user.uid);
        return user;
      });
    }
    return users;
  }
}
